import { formatDayFull } from '@krp/ui-kit'

import type { CalendarDay } from './calendar'
import { cashierColor, type Swatch } from './colors'
import type { Assignment, AssignmentKind, IsoDay, Place } from './types'

/**
 * Таблица графика: строки, столбцы — дни месяца, клетка на пересечении «кто × где × когда». Один вид на два экрана: в таблице
 * объекта строки — кассиры состава, в «По кассирам» строки — объекты и один выбранный кассир. Всё здесь — чистые функции над
 * отметками ВСЕХ объектов за месяц; «сохраняется…» приходит параметром — это состояние отправленной команды, а не данных.
 * Считается и занимает день только отметка «выходит»: «выходной день» и «не выходит» ни итог, ни занятость не меняют.
 */

/** Ключ клетки: объект, кассир (строчными) и день. */
export const cellKey = (placeId: string, cashierId: string, day: IsoDay): string => `${placeId.toLowerCase()}|${cashierId.toLowerCase()}|${day}`

/** Вид клетки: отметка из записи службы или `none` — записи нет, клетка не отмечена. */
export type CellKind = AssignmentKind | 'none'

export interface GridCell {
  day: CalendarDay
  kind: CellKind
  /** Название другого объекта, где кассир выходит в этот день; `null` — нигде. Поставить его «выходит» на этом объекте нельзя. */
  busyAt: string | null
  /** Команда по клетке отправлена и не подтверждена службой. */
  saving: boolean
  /** Описание для диктора и подсказки: кто, где, когда, какая отметка, занят ли, идёт ли сохранение. */
  label: string
}

export interface GridRow {
  /** Ключ строки: кассир (таблица объекта) или объект («По кассирам»). */
  key: string
  placeId: string
  cashierId: string
  /** Подпись слева: имя кассира или название объекта. */
  title: string
  /** Цвет кассира строки (в «По кассирам» — выбранного кассира): им закрашены клетки «выходит». */
  swatch: Swatch
  cells: readonly GridCell[]
  /** Итог справа («Всего»): сколько дней кассир выходит («Р») в этой строке; выходные и «не выходит» не считаются. */
  total: number
}

const byTitle = (left: string, right: string): number => left.localeCompare(right, 'ru')

const dayKey = (cashierId: string, day: IsoDay): string => `${cashierId.toLowerCase()}|${day}`

/** Где кассир выходит в какой день: ключ — кассир и день, значение — объекты. */
export type DayIndex = ReadonlyMap<string, readonly string[]>

const NO_PLACES: ReadonlySet<string> = new Set()

/**
 * `ignoredPlaces` — идентификаторы объектов строчными, которые занятость дня не создают: служба не считает занятым день, в который
 * кассир стоит на объекте в архиве, поэтому и клиент не должен запирать такую клетку.
 */
export function dayIndexOf(items: readonly Assignment[], ignoredPlaces: ReadonlySet<string> = NO_PLACES): DayIndex {
  const index = new Map<string, string[]>()
  for (const item of items) {
    if (item.kind !== 'work' || ignoredPlaces.has(item.placeId.toLowerCase())) continue
    const key = dayKey(item.cashierId, item.date)
    const known = index.get(key)
    if (known === undefined) index.set(key, [item.placeId])
    else if (!known.some((id) => id.toLowerCase() === item.placeId.toLowerCase())) known.push(item.placeId)
  }
  return index
}

/** Названия других объектов, на которых кассир выходит в этот день; `null` — нигде, кроме этого. */
export function busyAtOf(index: DayIndex, placeNameOf: (placeId: string) => string, placeId: string, cashierId: string, day: IsoDay): string | null {
  const others = (index.get(dayKey(cashierId, day)) ?? []).filter((id) => id.toLowerCase() !== placeId.toLowerCase()).map(placeNameOf)
  return others.length === 0 ? null : others.sort(byTitle).join(', ')
}

const KIND_WORDS: Readonly<Record<AssignmentKind, string>> = { work: 'выходит', day_off: 'выходной день', absent: 'не выходит (отмечено)' }

/** Описание клетки для диктора: кто (и где), день, какая отметка (или «не отмечено» / «занято: …»), идёт ли сохранение. */
export function gridCellLabel(subject: string, day: CalendarDay, kind: CellKind, busyAt: string | null, saving: boolean): string {
  const dayText = `${day.weekdayShort}, ${formatDayFull(day.day)}`
  const state = kind !== 'none' ? KIND_WORDS[kind] : busyAt !== null ? `занято: ${busyAt}` : 'не отмечено'
  return `${subject}, ${dayText}: ${state}${saving ? ', сохраняется…' : ''}`
}

interface RowInput {
  days: readonly CalendarDay[]
  items: readonly Assignment[]
  saving: ReadonlySet<string>
  placeNameOf: (placeId: string) => string
  /** Объекты в архиве (идентификаторы строчными): стоять на них не мешает ставить кассира на другой объект — как у службы. */
  archivedPlaceIds?: ReadonlySet<string>
}

function cellsOf(input: RowInput, index: DayIndex, kinds: ReadonlyMap<string, AssignmentKind>, placeId: string, cashierId: string, subject: string): GridCell[] {
  return input.days.map((day): GridCell => {
    const key = cellKey(placeId, cashierId, day.day)
    const kind: CellKind = kinds.get(key) ?? 'none'
    const busyAt = kind === 'work' ? null : busyAtOf(index, input.placeNameOf, placeId, cashierId, day.day)
    const saving = input.saving.has(key)
    return { day, kind, busyAt, saving, label: gridCellLabel(subject, day, kind, busyAt, saving) }
  })
}

const kindsOf = (items: readonly Assignment[]): Map<string, AssignmentKind> => new Map(items.map((item) => [cellKey(item.placeId, item.cashierId, item.date), item.kind]))

const workDays = (cells: readonly GridCell[]): number => cells.filter((cell) => cell.kind === 'work').length

export interface PlaceGridInput extends RowInput {
  placeId: string
  /** Состав объекта (хранит сервер): каждый получает строку, даже без единого выхода; кассиры с выходами на объекте строку получают и вне состава. */
  memberIds: readonly string[]
  nameOf: (cashierId: string) => string
}

/**
 * Таблица объекта: строки — весь состав объекта и кассиры, у которых на этом объекте есть выходы в показанных назначениях (ушедший
 * из состава кассир с прошлыми днями не пропадает из вида, а «занято» не остаётся необъяснимым), по алфавиту; идентификаторы
 * сравниваются без учёта регистра.
 */
export function placeGridOf(input: PlaceGridInput): GridRow[] {
  const index = dayIndexOf(input.items, input.archivedPlaceIds)
  const kinds = kindsOf(input.items)
  const unique = new Map<string, string>()
  for (const id of input.memberIds) if (!unique.has(id.toLowerCase())) unique.set(id.toLowerCase(), id)
  for (const item of input.items) {
    if (item.placeId.toLowerCase() === input.placeId.toLowerCase() && !unique.has(item.cashierId.toLowerCase())) unique.set(item.cashierId.toLowerCase(), item.cashierId)
  }
  const rows = [...unique.values()].map((cashierId): GridRow => {
    const name = input.nameOf(cashierId)
    const cells = cellsOf(input, index, kinds, input.placeId, cashierId, name)
    return { key: cashierId, placeId: input.placeId, cashierId, title: name, swatch: cashierColor(cashierId), cells, total: workDays(cells) }
  })
  return rows.sort((left, right) => byTitle(left.title, right.title))
}

export interface CashierGridInput extends RowInput {
  cashierId: string
  cashierName: string
  /** Активные объекты в том порядке, в каком они показываются. */
  places: readonly Place[]
}

/** «По кассирам»: строки — все активные объекты; клетка закрашена, если выбранный кассир выходит («Р») на этом объекте в этот день. */
export function cashierGridOf(input: CashierGridInput): GridRow[] {
  const index = dayIndexOf(input.items, input.archivedPlaceIds)
  const kinds = kindsOf(input.items)
  const swatch = cashierColor(input.cashierId)
  return input.places.map((place): GridRow => {
    const cells = cellsOf(input, index, kinds, place.id, input.cashierId, `${input.cashierName}, ${place.name}`)
    return { key: place.id, placeId: place.id, cashierId: input.cashierId, title: place.name, swatch, cells, total: workDays(cells) }
  })
}

/** Сколько разных кассиров выходит («Р») на каждом объекте в показанных назначениях (для плиток: период — сегодня). */
export function cashiersByPlace(items: readonly Assignment[]): ReadonlyMap<string, number> {
  const people = new Map<string, Set<string>>()
  for (const item of items) {
    if (item.kind !== 'work') continue
    const set = people.get(item.placeId) ?? new Set<string>()
    set.add(item.cashierId.toLowerCase())
    people.set(item.placeId, set)
  }
  return new Map([...people].map(([placeId, set]) => [placeId, set.size]))
}
